import { useEffect, useRef, useState } from "react";
import { Check, Crown } from "lucide-react";
import { Mascot } from "@/components/game/Mascot";
import { OPTION_COLORS } from "@/components/game/common";

export const optColor = (o) => o?.color || OPTION_COLORS[o?.id] || "#64748B";
const OK = ["Tebrikler!", "Harikasın!", "Süpersin!", "Muhteşem!", "Aferin sana!", "Bravo!"];
const NO = ["Hay aksi!", "Az kaldı!", "Bir dahakine!", "Pes etmek yok!", "Neredeyse!"];
export const praise = (ok, seed = 0) => (ok ? OK : NO)[Math.abs(seed) % (ok ? OK.length : NO.length)];

// Şık biçiminde sonuç: içi doğru yüzdesi kadar şıkkın renginde dolar.
export const ResultTile = ({ o, correct, mine, big, testId }) => {
  const c = optColor(o);
  return (
    <div className={`relative overflow-hidden border-2 flex items-center ${big ? "rounded-[1.5rem] gap-[1vw] px-[1.4vw] py-[1.6vh]" : "rounded-xl gap-3 p-3"} ${correct ? "anim-pop" : ""}`}
      style={{ borderColor: correct ? "#22C55E" : `${c}aa`, boxShadow: correct ? "0 0 26px rgba(34,197,94,0.45)" : `0 5px 0 ${c}44`, background: "#161C27" }} data-testid={testId}>
      <div className="absolute inset-y-0 left-0 fc-fill" style={{ width: `${o.pct}%`, background: `linear-gradient(90deg, ${c}cc, ${c}77)` }} />
      <span className={`relative shrink-0 grid place-items-center uppercase font-display font-bold text-[#0B0F16] ${big ? "h-[5.5vh] w-[5.5vh] rounded-2xl text-[3vh]" : "h-9 w-9 rounded-md"}`} style={{ backgroundColor: c }}>{o.id}</span>
      <span className={`relative flex-1 font-bold min-w-0 ${big ? "text-[min(2.1rem,3.1vh)]" : "text-base"}`}>{o.text}{mine && <span className="ml-2 text-xs rounded-full bg-black/40 px-2 py-0.5 align-middle">senin</span>}</span>
      {correct && <Check className={`relative text-[#4ADE80] shrink-0 ${big ? "h-[4vh] w-[4vh]" : "h-6 w-6"}`} />}
      <span className={`relative font-display font-bold shrink-0 ${big ? "text-[3.2vh]" : "text-lg"}`}>%{o.pct}{o.count != null && <span className="opacity-70 text-[0.7em] ml-1">({o.count})</span>}</span>
    </div>
  );
};

const lastRanks = { current: {} };
// Sonuç ekranında sağda podyum havasında dinamik sıralama: önce önceki sıra, sonra yeni sıra ve puan.
export const ScoreRace = ({ rows, title = "Puan durumu" }) => {
  const sorted = [...rows].sort((a, b) => b.score - a.score);
  const prev = useRef(lastRanks.current);
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSettled(true), 700);
    lastRanks.current = Object.fromEntries(sorted.map((r, i) => [r.id, { rank: i, score: r.score }]));
    return () => clearTimeout(t);
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const ROW = 7.4;
  const top = Math.max(sorted[0]?.score || 0, 1);
  return (
    <div className="panel !rounded-[1.75rem] p-[1.4vh] flex flex-col min-h-0" data-testid="board-score-race">
      <p className="eyebrow !text-[1.6vh] mb-[1vh]">{title}</p>
      <div className="relative" style={{ height: `${sorted.length * ROW}vh` }}>
        {sorted.map((r, i) => {
          const p = prev.current[r.id];
          const rank = settled || !p ? i : p.rank;
          const score = settled || !p ? r.score : p.score;
          const medal = ["#FACC15", "#E2E8F0", "#FB923C"][rank];
          return (
            <div key={r.id} className="absolute inset-x-0 transition-[top] duration-700 ease-out" style={{ top: `${rank * ROW}vh`, height: `${ROW - 0.9}vh` }} data-testid={`race-row-${r.id}`}>
              <div className="relative h-full rounded-2xl overflow-hidden border-2 flex items-center gap-[0.7vw] px-[0.8vw]" style={{ borderColor: medal || `${r.color}55`, background: "#121826" }}>
                <div className="absolute inset-y-0 left-0 transition-[width] duration-700" style={{ width: `${(100 * score) / top}%`, background: `linear-gradient(90deg, ${r.color}55, ${r.color}18)` }} />
                <span className="relative font-display text-[2.8vh] font-bold w-[3.4vh]" style={{ color: medal || "#CBD5E1" }}>{rank + 1}.</span>
                {r.mascot && <Mascot id={r.mascot} size="3.6vh" className="relative" />}
                <span className="relative font-display text-[min(1.7rem,2.5vh)] font-bold flex-1 truncate" style={{ color: r.mascot ? "#fff" : r.color }}>{r.name}</span>
                {rank === 0 && score > 0 && <Crown className="relative h-[2.8vh] w-[2.8vh] text-[#FACC15] anim-bob" />}
                <span key={score} className="relative font-display text-[3vh] font-bold anim-pop tabular-nums">{score}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
